import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  CheckCircle2,
  Clock,
  AlertTriangle,
  ListTodo,
  Timer,
  TrendingUp,
  Calendar,
  Loader2,
  ArrowRight,
  ClipboardList
} from 'lucide-react';
import { format } from 'date-fns';
import ExportPdfButton from '../components/ExportPdfButton';

const DashboardPage = () => {
  const { user, api, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [weeklyProgress, setWeeklyProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, progressRes] = await Promise.allSettled([
        api.get('/dashboard/stats'),
        api.get('/progress/weekly')
      ]);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (progressRes.status === 'fulfilled') setWeeklyProgress(progressRes.value.data);
    } catch (error) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const myTasks = stats?.my_tasks || {};
  const totalTasks = myTasks.total || 0;
  // Ongoing tasks never complete, so they would drag the completion rate down.
  const completableTasks = totalTasks - (myTasks.ongoing || 0);
  const completedPct = completableTasks > 0 ? Math.round(((myTasks.completed || 0) / completableTasks) * 100) : 0;
  // Server-generated dashboard PDF for Export PDF.
  const dashboardReport = {
    path: '/dashboard/stats/pdf',
    filename: `syncly-dashboard-${format(new Date(), 'yyyy-MM-dd')}.pdf`,
  };
  const navigateToTasks = ({ status = 'all', mine = false } = {}) => {
    const params = new URLSearchParams();
    if (mine) params.set('mine', 'true');
    if (status && status !== 'all') params.set('status', status);
    const query = params.toString();
    navigate(query ? `/tasks?${query}` : '/tasks');
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl tracking-tight uppercase">DASHBOARD</h1>
          <p className="text-muted-foreground text-sm">
            Welcome back, {user?.name}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-between md:justify-end gap-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            {format(new Date(), 'EEEE, MMMM d, yyyy')}
          </div>
          <ExportPdfButton api={api} {...dashboardReport} testId="dashboard-export-pdf-btn" />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
        {/* Total Tasks */}
        <Card
          className="border-border hover:border-primary/50 transition-colors cursor-pointer"
          onClick={() => navigateToTasks({ mine: true })}
        >
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center gap-2 mb-2">
              <ListTodo className="h-4 w-4 text-muted-foreground" />
              <span className="text-[10px] md:text-xs uppercase tracking-wider text-muted-foreground">MY TASKS</span>
            </div>
            <p className="text-2xl md:text-4xl font-bold">{totalTasks}</p>
            <p className="text-[10px] md:text-xs text-muted-foreground mt-1">Total Assigned</p>
          </CardContent>
        </Card>

        {/* Hours This Week */}
        <Card className="border-border hover:border-primary/50 transition-colors">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center gap-2 mb-2">
              <Timer className="h-4 w-4 text-muted-foreground" />
              <span className="text-[10px] md:text-xs uppercase tracking-wider text-muted-foreground">HOURS</span>
            </div>
            <p className="text-2xl md:text-4xl font-bold">{stats?.my_hours_this_week?.toFixed(1) || '0'}</p>
            <p className="text-[10px] md:text-xs text-muted-foreground mt-1">This Week</p>
          </CardContent>
        </Card>

        {/* Completed */}
        <Card
          className="border-border hover:border-primary/50 transition-colors cursor-pointer"
          onClick={() => navigateToTasks({ status: 'completed', mine: true })}
        >
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span className="text-[10px] md:text-xs uppercase tracking-wider text-muted-foreground">COMPLETED</span>
            </div>
            <p className="text-2xl md:text-4xl font-bold text-green-500">{myTasks.completed || 0}</p>
            <p className="text-[10px] md:text-xs text-muted-foreground mt-1">{completedPct}% Complete</p>
          </CardContent>
        </Card>

        {/* Overdue */}
        <Card
          className={`border-border hover:border-primary/50 transition-colors cursor-pointer ${myTasks.overdue > 0 ? 'border-destructive' : ''}`}
          onClick={() => navigateToTasks({ status: 'overdue', mine: true })}
        >
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              <span className="text-[10px] md:text-xs uppercase tracking-wider text-muted-foreground">OVERDUE</span>
            </div>
            <p className={`text-2xl md:text-4xl font-bold ${myTasks.overdue > 0 ? 'text-destructive' : ''}`}>
              {myTasks.overdue || 0}
            </p>
            <p className="text-[10px] md:text-xs text-muted-foreground mt-1">Past Due</p>
          </CardContent>
        </Card>

        {/* Pending Weekly Updates */}
        <Card className="col-span-2 md:col-span-1 border-border hover:border-primary/50 transition-colors">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center gap-2 mb-2">
              <ClipboardList className="h-4 w-4 text-amber-500" />
              <span className="text-[10px] md:text-xs uppercase tracking-wider text-muted-foreground">PENDING UPDATES</span>
            </div>
            <p className="text-2xl md:text-4xl font-bold">{stats?.pending_weekly_updates || 0}</p>
            <button
              onClick={() => navigate('/weekly-sync')}
              className="text-[10px] md:text-xs text-primary hover:underline mt-1"
            >
              Submit weekly updates →
            </button>
          </CardContent>
        </Card>
      </div>

      {/* Task Breakdown + Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Task Status Breakdown */}
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <ListTodo className="h-4 w-4" />
              TASK BREAKDOWN
            </CardTitle>
          </CardHeader>
          <CardContent>
            {totalTasks > 0 ? (
              <div className="space-y-3">
                {/* Progress bar */}
                <div className="h-3 bg-muted overflow-hidden flex">
                  {(myTasks.completed || 0) > 0 && (
                    <div className="h-full bg-green-500 transition-all" style={{ width: `${Math.round(((myTasks.completed || 0) / totalTasks) * 100)}%` }} />
                  )}
                  {(myTasks.in_progress || 0) > 0 && (
                    <div className="h-full bg-primary transition-all" style={{ width: `${Math.round(((myTasks.in_progress || 0) / totalTasks) * 100)}%` }} />
                  )}
                  {(myTasks.pending || 0) > 0 && (
                    <div className="h-full bg-muted-foreground/30 transition-all" style={{ width: `${Math.round(((myTasks.pending || 0) / totalTasks) * 100)}%` }} />
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div
                    className="flex items-center justify-between p-3 border border-border cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => navigateToTasks({ status: 'completed', mine: true })}
                  >
                    <div className="flex items-center gap-2">
                      <div className="h-2.5 w-2.5 bg-green-500" />
                      <span className="text-sm">Completed</span>
                    </div>
                    <span className="text-sm font-bold">{myTasks.completed || 0}</span>
                  </div>
                  <div
                    className="flex items-center justify-between p-3 border border-border cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => navigateToTasks({ status: 'in_progress', mine: true })}
                  >
                    <div className="flex items-center gap-2">
                      <div className="h-2.5 w-2.5 bg-primary" />
                      <span className="text-sm">In Progress</span>
                      {myTasks.ongoing > 0 && (
                        <span className="text-[10px] text-muted-foreground" title="Ongoing tasks never complete">
                          incl. {myTasks.ongoing} ongoing
                        </span>
                      )}
                    </div>
                    <span className="text-sm font-bold">{myTasks.in_progress || 0}</span>
                  </div>
                  <div
                    className="flex items-center justify-between p-3 border border-border cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => navigateToTasks({ status: 'pending', mine: true })}
                  >
                    <div className="flex items-center gap-2">
                      <div className="h-2.5 w-2.5 bg-muted-foreground/30" />
                      <span className="text-sm">Pending</span>
                    </div>
                    <span className="text-sm font-bold">{myTasks.pending || 0}</span>
                  </div>
                  <div
                    className="flex items-center justify-between p-3 border border-border cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => navigateToTasks({ status: 'overdue', mine: true })}
                  >
                    <div className="flex items-center gap-2">
                      <div className="h-2.5 w-2.5 bg-destructive" />
                      <span className="text-sm">Overdue</span>
                    </div>
                    <span className="text-sm font-bold">{myTasks.overdue || 0}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <ClipboardList className="h-10 w-10 text-muted-foreground/40 mb-3" />
                <p className="text-sm text-muted-foreground mb-3">No tasks assigned yet</p>
                <Button variant="outline" size="sm" onClick={() => navigateToTasks({ mine: true })}>
                  <ArrowRight className="h-4 w-4 mr-2" />
                  GO TO TASKS
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Deadlines */}
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />
              UPCOMING DEADLINES
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.upcoming_deadlines?.length > 0 ? (
              <div className="space-y-2">
                {stats.upcoming_deadlines.map((task) => (
                  <div
                    key={task.id}
                    className="flex items-center justify-between p-3 border border-border hover:border-primary/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/tasks/${task.id}`)}
                    data-testid={`upcoming-task-${task.id}`}
                  >
                    <span className="text-sm truncate mr-3">{task.title}</span>
                    <Badge variant="outline" className="text-xs shrink-0">
                      {task.deadline ? format(new Date(task.deadline), 'MMM d') : 'No date'}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <CheckCircle2 className="h-10 w-10 text-green-500/40 mb-3" />
                <p className="text-sm text-muted-foreground">No upcoming deadlines</p>
                <p className="text-xs text-muted-foreground/70 mt-1">You're all caught up</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Weekly Team Progress - Always visible */}
      <Card className="border-border">
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2 flex-wrap">
              <TrendingUp className="h-4 w-4 shrink-0" />
              <span>TEAM PROGRESS</span>
              {weeklyProgress && (
                <span className="text-muted-foreground/70 font-normal text-xs">
                  {weeklyProgress.week_start} - {weeklyProgress.week_end}
                </span>
              )}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/weekly-sync')} className="text-xs shrink-0">
              <ArrowRight className="h-3 w-3" />
              <span className="hidden sm:inline ml-1">VIEW DETAILS</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {weeklyProgress?.progress?.length > 0 ? (
            <div className="overflow-x-auto -mx-4 md:mx-0">
              <table className="w-full text-sm min-w-[400px] table-fixed">
                <colgroup>
                  <col className="w-[40%]" />
                  <col className="w-[20%]" />
                  <col className="w-[20%]" />
                  <col className="w-[20%]" />
                </colgroup>
                <thead>
                  <tr className="border-b border-border">
                    <th className="table-header !text-left pl-2 md:pl-4">MEMBER</th>
                    <th className="table-header !text-center">HOURS</th>
                    <th className="table-header !text-center">DONE</th>
                    <th className="table-header !text-center">PENDING</th>
                  </tr>
                </thead>
                <tbody>
                  {weeklyProgress.progress.map((member) => (
                    <tr key={member.user_id} className="table-row" data-testid={`team-member-${member.user_id}`}>
                      <td className="py-2 pl-2 pr-2 md:py-3 md:pl-4 md:pr-4">
                        <div className="flex items-center gap-2">
                          <div className="h-6 w-6 md:h-7 md:w-7 bg-primary/10 flex items-center justify-center text-primary text-xs font-bold shrink-0">
                            {member.user_name.charAt(0).toUpperCase()}
                          </div>
                          <span className="truncate max-w-[120px] md:max-w-none">{member.user_name}</span>
                        </div>
                      </td>
                      <td className="py-2 px-2 md:py-3 md:px-4 text-center font-mono">{member.total_hours.toFixed(1)}h</td>
                      <td className="py-2 px-2 md:py-3 md:px-4 text-center">
                        <Badge variant="success" className="text-xs">{member.tasks_completed}</Badge>
                      </td>
                      <td className="py-2 px-2 md:py-3 md:px-4 text-center">
                        <Badge variant="outline" className="text-xs">{member.tasks_pending}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <TrendingUp className="h-10 w-10 text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">No team activity this week</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Progress will appear here once team members start logging work
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
